import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';

import { PseudoLink } from '~components/Helpers/PseudoLink';
import AttachmentCard, {
  attachmentCardSx,
} from '~pages/Messages/components/AttachmentCard';
import {
  MESSAGING_BUBBLE_MAX_WIDTH,
  MESSAGING_BUBBLE_PADDING_X,
} from '~pages/Messages/messagingBubble';
import { MessagingOutboxEntry } from '~pages/Messages/MessagingOutboxContext';

type OutboxMessageRowProps = {
  entry: MessagingOutboxEntry;
  onRetry: (clientMessageId: string) => void;
};

export default function OutboxMessageRow({ entry, onRetry }: OutboxMessageRowProps) {
  const theme = useTheme();

  return (
    <Box
      data-test-id="messaging-outbox-message"
      sx={{
        alignItems: 'flex-end',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        data-test-id="messaging-outbox-message-bubble"
        sx={{
          backgroundColor: theme.semantic.chipInfoBg,
          border: entry.hasFailed ? `1px solid ${theme.semantic.errorText}` : undefined,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.25,
          maxWidth: MESSAGING_BUBBLE_MAX_WIDTH,
          minWidth: 0,
          px: MESSAGING_BUBBLE_PADDING_X,
          py: 1,
        }}
      >
        {entry.content.length > 0 && (
          <Typography
            data-test-id="messaging-outbox-message-body"
            sx={{
              color: entry.hasFailed ? undefined : theme.semantic.textSecondary,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
            variant="body2"
          >
            {entry.content}
          </Typography>
        )}

        {/* Named rather than previewed: the composer revokes its object URLs on send. */}
        {entry.attachments.map((attachment) => (
          <Box key={attachment.signedId} sx={{ ...attachmentCardSx(theme), mt: 0.875 }}>
            <AttachmentCard
              fileName={attachment.filename}
              isImage={attachment.isImage}
              isPending={!entry.hasFailed}
            />
          </Box>
        ))}

        {!entry.hasFailed && (
          <Typography
            sx={{ alignSelf: 'flex-end', color: theme.semantic.textSecondary }}
            variant="caption"
          >
            {t('messaging.sending')}
          </Typography>
        )}
      </Box>

      {entry.hasFailed && (
        <Box sx={{ alignItems: 'baseline', display: 'flex', gap: 1, pt: 0.25 }}>
          <Typography sx={{ color: theme.semantic.errorText }} variant="caption">
            {t('messaging.send_failed')}
          </Typography>

          <PseudoLink
            action={() => onRetry(entry.clientMessageId)}
            dataTestId="messaging-outbox-retry"
            linkColor={theme.semantic.errorText}
            title={t('actions.retry')}
          />
        </Box>
      )}
    </Box>
  );
}
